import { Button, Chip, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import Icon from '@components/Icon/Icon';

import { useUpdateAlertStatuses } from '@hooks/api/alerts/alert';
import { useSession } from '@hooks/useSession';

import { capitalize } from '@utils/format';

import { AlertStatus } from 'generated/types';

const messages = defineMessages({
  clearAlert: {
    id: 'alertButtonChip.button.clearAlert',
    defaultMessage: 'Clear Alert',
  },
  openAlert: {
    id: 'alertButtonChip.chip.openAlert',
    defaultMessage: 'Open Alert',
  },
  clearedAlert: {
    id: 'alertButtonChip.chip.clearedAlert',
    defaultMessage: 'Cleared Alert',
  },
  archivedAlert: {
    id: 'alertButtonChip.chip.archivedAlert',
    defaultMessage: 'Archived Alert',
  },
});

interface AlertButtonChipProps {
  alert_id: string;
  status: AlertStatus;
  /** Used for BP only: Displays if alert is for sys or dia */
  prefix?: string | null;
}

/**
 * Alert button or chip component used in metric drilldown
 *
 * Practitioners: Button to update alert status
 * Other roles: Read-only to view observation's alert status
 */
export default function AlertButtonChip({
  alert_id,
  status,
  prefix,
}: AlertButtonChipProps) {
  const { formatMessage } = useIntl();
  const {
    state: { isPractitioner },
  } = useSession();

  const getFriendlyStatus = (): MessageDescriptor => {
    switch (status) {
      case AlertStatus.open:
        return messages.openAlert;
      case AlertStatus.cleared:
        return messages.clearedAlert;
      case AlertStatus.archived:
        return messages.archivedAlert;
    }
  };

  const isOpenStatus = status === AlertStatus.open;

  const [updateAlert, loading] = useUpdateAlertStatuses();

  const handleClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    await updateAlert({
      alert_ids: [alert_id],
      status: AlertStatus.cleared,
    });
  };

  if (isPractitioner) {
    return (
      <Button
        color="primary"
        disabled={!isOpenStatus || loading}
        onClick={handleClick}
        startIcon={
          <Icon
            name={loading ? 'spinner-third' : 'triangle-exclamation'}
            spin={loading}
            type={loading ? 'fad' : 'fas'}
          />
        }
        sx={{ borderRadius: '29px', textTransform: 'capitalize' }}
        variant="outlined"
      >
        {prefix ? `${capitalize(prefix)}: ` : ''}
        {formatMessage(messages.clearAlert)}
      </Button>
    );
  }

  return (
    <Chip
      icon={
        <Icon
          color="primary.main"
          name={isOpenStatus ? 'triangle-exclamation' : 'check'}
          size="xl"
          type="fas"
        />
      }
      label={
        <Typography color="text.secondary" variant="body2">
          {formatMessage(getFriendlyStatus())}
        </Typography>
      }
      sx={{
        borderColor: 'primary.main',
        textTransform: 'capitalize',
        px: 0.5,
        py: 0.5,
        height: 'auto',
        '& .MuiChip-label': {
          display: 'block',
          whiteSpace: 'normal',
        },
      }}
      variant="outlined"
    />
  );
}
